<template>
  <section>
    <div class="textarea__wrap">
      <ui-textarea
        ref="textareaRef"
        v-model="changeUrl"
        :error-messages="errorMessages"
        :hide-details="2"
        :rows="areaRows"
        :disabled="disabled"
        :placeholder="placeholder"
        @focus="onFocus"
      >
        <template #label>
          <label class="mb-4">
            {{ label }}
            <ui-tooltip-icon
              v-if="showToolTip"
              preset="help"
              class="mb-2"
              :text="tooltip"
            />
            <v-menu
              v-if="currentUrl && showQr"
              location="bottom"
              open-on-hover
              :offset="[0, 8]"
              :transition="false"
            >
              <template #activator="{ props: menuProps }">
                <a v-bind="menuProps">
                  <ui-icon
                    size="13"
                    :color="$colors.blue"
                    class="ml-8"
                    icon="fas fa-qrcode"
                  />
                  {{ t('QR') }}
                </a>
              </template>
              <v-list>
                <span v-html="qrLink" />
              </v-list>
            </v-menu>
          </label>
        </template>
      </ui-textarea>
    </div>
    <div class="mb-14 mt-10 item-group__wrap">
      <i>{{ t('Available tokens:') }}</i>
      <v-chip
        v-for="item in availableTokens"
        :key="item.tag"
        class="tag"
        variant="flat"
        :disabled="item.disabled || disabled"
        :color="$colors.green"
        @click="clickChip(item)"
      >
        {{ item.tag }}
      </v-chip>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useColors, useI18n } from '@/composables';

interface UrlTagChip {
  tag: string;
  disabled?: boolean;
}

const props = withDefaults(
  defineProps<{
    modelValue?: string;
    url?: string;
    chips?: UrlTagChip[];
    placeholder?: string;
    label?: string;
    showToolTip?: boolean;
    toolTip?: string;
    showQr?: boolean;
    textAreaRowsCount?: number;
    disabled?: boolean;
    hasErrors?: boolean;
  }>(),
  {
    showToolTip: true,
    showQr: true,
    disabled: false,
    hasErrors: false,
  },
);

const emit = defineEmits<{
  change: [value: string];
  'update:modelValue': [value: string];
  'update:hasErrors': [value: boolean];
}>();

const currentUrl = computed(() =>
  props.modelValue !== undefined ? props.modelValue : (props.url ?? ''),
);

const { t } = useI18n();
const $colors = useColors();

const textareaRef = ref<{ $el?: HTMLElement } | null>(null);

const areaRows = computed(() => props.textAreaRowsCount ?? 5);

const errorMessages = computed(() => (props.hasErrors ? 'error' : null));

const tooltip = computed(
  () =>
    props.toolTip
    || t('It is required to use the {clickId} token if you want to track conversions.'),
);

const qrLink = computed(
  () =>
    '<img src="https://api.qrserver.com/v1/create-qr-code/?size=177x177&margin=10&data='
    + (currentUrl.value ? encodeURIComponent(String(currentUrl.value).trim()) : '')
    + '" alt="QR Code" border="0">',
);

const setUrl = (value: string) => {
  emit('update:modelValue', value);
  emit('change', value);
  emit('update:hasErrors', false);
};

const changeUrl = computed({
  get: () => currentUrl.value,
  set: (value: string) => {
    setUrl(value);
  },
});

const availableTokens = computed((): UrlTagChip[] => {
  if (!currentUrl.value) {
    return props.chips ?? [];
  }
  return (props.chips ?? []).map((item) => {
    const token = item.tag.replace('+ ', '');
    return {
      ...item,
      disabled: Boolean(currentUrl.value.indexOf(token) + 1),
    };
  });
});

const changePostbackUrl = () => {
  emit('change', currentUrl.value);
};

watch(
  () => props.chips,
  () => {
    changePostbackUrl();
  },
);

const onFocus = () => {
  emit('update:hasErrors', false);
};

const clickChip = (item: UrlTagChip) => {
  if (item.disabled || props.disabled) {
    return;
  }

  const root = textareaRef.value?.$el;
  const textArea = root?.getElementsByTagName('textarea');
  if (!textArea?.[0]) {
    return;
  }

  const { selectionStart: startPos, selectionEnd: endPos, value: tmpStr } = textArea[0];

  let nextUrl = tmpStr.substring(0, startPos) + item.tag + tmpStr.substring(endPos, tmpStr.length);
  nextUrl = nextUrl.replace('+ ', '');
  setUrl(nextUrl);

  textArea[0].focus();
};
</script>

<style lang="scss" scoped>
.tag {
  :deep(.v-chip__content) {
    cursor: pointer;
  }
}
</style>

<style lang="scss">
.textarea__wrap {
  > label {
    font-size: 13px;
    font-weight: bold;
  }

  .v-input.v-textarea {
    padding-top: 6px !important;
  }
}

.item-group__wrap {
  .v-chip {
    display: inline-flex;
    height: 16px !important;
    margin: 0 2px !important;
    font-size: 11px;
    line-height: 16px;
    vertical-align: middle;

    .v-chip__content {
      height: 16px !important;
      line-height: 16px;
      font-size: 11px;
      color: var(--color-white);
      cursor: pointer;
    }
  }

  > i {
    margin-right: 2px;
  }

  .v-chip.v-chip--disabled {
    opacity: 1 !important;
    background: var(--color-gray10) !important;
    border: 0 !important;

    .v-chip__content {
      color: var(--color-black);
    }
  }
}
</style>
